<template>
  <ds-card
    title="Block specific days"
    subtitle="Block a few hours, a whole day, or several days. You won't receive new dates during a blocked period, and any dates already planned for it are moved."
  >
    <div class="lao-notes">
      <div class="lao-note warn">
        <strong>Moving a date happening in under 8 hours?</strong> Add the blocked period here and also email
        <a href="mailto:partners@breeze.social">partners@breeze.social</a> telling us which date(s) you can't
        accommodate. At that notice we need to make sure couples have time to adjust their plans.
      </div>
      <div class="lao-note">
        It can take up to 60 minutes before reservations are removed.
      </div>
    </div>

    <div
      v-for="(slot, index) in editData"
      :key="index"
      class="lao-block"
    >
      <div class="lao-grid">
        <div class="lao-when">
          <div class="lao-when-label">
            From
          </div>
          <div class="lao-when-inputs">
            <b-datepicker
              v-model="slot.fromParsed"
              :min="getMin(new Date())"
              :date-format-options="dateLabelFormat"
              placeholder="Add a date"
              @input="setUpdated(editData, index, true)"
            />
            <input
              v-model="slot.fromTimeParsed"
              class="lao-time"
              type="time"
              aria-label="Blocked from time"
              @input="setUpdated(editData, index, true)"
            >
          </div>
        </div>

        <div class="lao-when">
          <div class="lao-when-label">
            Until
          </div>
          <div class="lao-when-inputs">
            <b-datepicker
              v-model="slot.untilParsed"
              type="datetime"
              :min="getMin(slot.fromDate)"
              :date-format-options="dateLabelFormat"
              placeholder="Add a date"
              @input="setUpdated(editData, index)"
            />
            <input
              v-model="slot.untilTimeParsed"
              class="lao-time"
              type="time"
              aria-label="Blocked until time"
              @input="setUpdated(editData, index)"
            >
          </div>
        </div>

        <div class="lao-actions">
          <ds-button
            v-if="slot.updated && slot.fromDate && slot.untilDate"
            size="sm"
            :disabled="slot.updating"
            @click="save(editData, index)"
          >
            {{ slot.updating ? 'Saving…' : 'Save' }}
          </ds-button>
          <ds-button
            v-if="!slot.updating"
            variant="danger"
            size="sm"
            @click="remove(editData, index)"
          >
            Remove
          </ds-button>
        </div>
      </div>

      <textarea
        v-model="slot.reason"
        class="lao-reason"
        rows="2"
        placeholder="Reason (optional)"
        aria-label="Reason for the block"
        @input="slot.updated = true"
      />
    </div>

    <p
      v-if="editData.length === 0"
      class="lao-empty"
    >
      No blocked days yet.
    </p>

    <ds-button
      variant="secondary"
      @click="add"
    >
      + Add a blocked period
    </ds-button>
  </ds-card>
</template>

<script>

import { DsButton, DsCard } from '@/design-system/components'
import { LocationAvailabilityOverride } from '@/_models/location-availability-override'
import { formatDate } from '@/_helpers/util'
import { event } from 'vue-gtag'

export default {
  name: 'LocationAvailabilityOverrides',
  components: {
    DsButton,
    DsCard,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    locationId: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      editData: [],
      // Display only: the picker still emits its yyyy-MM-dd string.
      dateLabelFormat: { year: 'numeric', month: 'short', day: 'numeric' },
    }
  },
  watch: {
    value: {
      handler() {
        // Our own emit coming back through v-model: re-filtering it would drop a row whose
        // half-typed Until briefly reads as already past.
        if (this.value !== this.editData) this.setData()
      },
    },
  },
  created() {
    this.setData()
  },
  methods: {
    // Surfaces the backend's message, falling back to a generic one.
    showError(error, fallback) {
      const message = (error && (
        (error.errorData && error.errorData.message)
        || error.message
        || (error.data && error.data.message)
      )) || (typeof error === 'string' ? error : fallback)
      this.$notify.error(`${message}`, { noAutoHide: true })
    },
    setData() {
      this.editData = this.value.filter(override => {
        const now = new Date()
        override.fromParsed = formatDate('yyyy-MM-dd', override.fromDate)
        override.fromTimeParsed = formatDate('hh:mm', override.fromDate)
        override.untilParsed = formatDate('yyyy-MM-dd', override.untilDate)
        override.untilTimeParsed = formatDate('hh:mm', override.untilDate)
        return override.untilDate > now
      })
    },
    getMin(min) {
      return formatDate('yyyy-MM-dd', (min) ?? new Date())
    },
    setUpdated(editData, index, fromChanged = false) {
      this.$set(this.editData[index], 'updated', true)

      // Parse fromDate and untilDate properly with time
      const fromDateStr = `${this.editData[index].fromParsed}T${this.editData[index].fromTimeParsed}`
      const untilDateStr = `${this.editData[index].untilParsed}T${this.editData[index].untilTimeParsed}`

      this.$set(this.editData[index], 'fromDate', new Date(fromDateStr))
      this.$set(this.editData[index], 'untilDate', new Date(untilDateStr))

      // From edits only: a time input emits each typed digit, so correcting Until mid-typing would clobber it.
      if (fromChanged && this.editData[index].fromDate >= this.editData[index].untilDate) {
        const adjustedUntilDate = new Date(this.editData[index].fromDate.getTime() + 3600000) // Add 1 hour
        this.$set(this.editData[index], 'untilDate', adjustedUntilDate)
        this.$set(this.editData[index], 'untilParsed', formatDate('yyyy-MM-dd', adjustedUntilDate))
        this.$set(this.editData[index], 'untilTimeParsed', formatDate('hh:mm', adjustedUntilDate))
      }

      this.$emit('input', this.editData)
    },
    add() {
      const item = new LocationAvailabilityOverride()
      item.fromDate = new Date()
      item.fromDate.setDate(item.fromDate.getDate() + 7)
      item.fromDate.setHours(0, 0, 0, 0)
      item.untilDate = new Date()
      item.untilDate.setDate(item.untilDate.getDate() + 7)
      item.untilDate.setHours(23, 59, 0, 0)
      item.fromParsed = formatDate('yyyy-MM-dd', item.fromDate)
      item.fromTimeParsed = formatDate('hh:mm', item.fromDate)
      item.untilParsed = formatDate('yyyy-MM-dd', item.untilDate)
      item.untilTimeParsed = formatDate('hh:mm', item.untilDate)
      item.updated = true
      item.locationId = this.locationId
      item.reason = ''

      this.editData.push(item)
      this.setUpdated(this.editData, this.editData.length - 1)
    },
    remove(editData, index) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure?')) {
        const block = this.editData[index]
        if (block.id) {
          block.deleteThis().catch(error => {
            this.showError(error, 'Could not remove the blocked period. Please try again.')
          })
        }
        this.editData.splice(index, 1)
        this.$emit('input', this.editData)
      }
    },
    save(editData, index) {
      // Not `>=`: a cleared time field is an Invalid Date, which compares false both ways.
      if (!(editData[index].fromDate < editData[index].untilDate)) {
        this.$notify.blocked('The until date needs to be after the from date.')
        return
      }

      const duration = (editData[index].untilDate - editData[index].fromDate) / (1000 * 60 * 60)
      if (duration < 2 && !window.confirm('Are you sure you want to create a block smaller than 2 hours?')) {
        return
      }

      this.$set(this.editData[index], 'updating', true)

      const block = editData[index]
      block.save().then(() => {
        this.$set(block, 'updated', false)
        this.$set(block, 'updating', false)
      }).catch(error => {
        // Leaving `updating` true would strand the row: Save stuck on "Saving…" and
        // disabled, Remove hidden, no way back without a reload.
        this.$set(block, 'updating', false)
        this.showError(error, 'Could not save the blocked period. Please try again.')
      })

      event('save-availability-override', {
        from: editData[index].fromDate,
        until: editData[index].untilDate,
      })
      this.$emit('input', this.editData)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.lao-notes {
  @include ds-stack(8px);

  margin-bottom: 20px;
}

.lao-note {
  @include ds-inset-row;

  padding: 12px 15px;
  font-size: 12.5px;
  line-height: 1.6;
  color: $c-text-sec;

  strong { color: $c-text; }

  a { color: $c-orange; }

  &.warn {
    background: $c-orange-light;
    border-color: rgba($c-orange, 0.22);
    border-left: 3px solid $c-orange;
  }
}

.lao-block {
  @include ds-inset-row;

  padding: 16px 18px;
  margin-bottom: 10px;
}

.lao-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
}

.lao-when {
  min-width: 0;
  flex: 1 1 260px;
}

.lao-when-label {
  font-size: 12px;
  font-weight: 600;
  color: $c-text-sec;
  margin-bottom: 5px;
}

.lao-when-inputs {
  display: flex;
  gap: 8px;

  ::v-deep .b-form-datepicker {
    @include ds-datepicker;
  }
}

.lao-time {
  @include ds-field;

  width: 108px;
  height: $ds-control-h;
  flex: 0 0 auto;

  &:focus { @include ds-focus-ring; }
}

.lao-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding-bottom: 1px;
}

.lao-reason {
  @include ds-field;

  width: 100%;
  margin-top: 12px;
  line-height: 1.6;
  resize: vertical;

  &::placeholder { color: $c-text-ter; }

  &:focus { @include ds-focus-ring; }
}

.lao-empty {
  margin: 0 0 14px;
  font-size: 12.5px;
  font-style: italic;
  color: $c-text-sec;
}
</style>
